import { devtools, persist } from 'zustand/middleware';
import { createWithEqualityFn as create } from 'zustand/traditional';
import { createSafeJSONStorage } from '../../helpers/storage/persist-storage';
import { isLoggingEnabled, logger } from '../logger/logger';
import type { State, WidgetMetadata } from './dashboards-store.type';
import { DashboardTab } from './dashboards-store.type';

const initialState: State = {
  selectedTab: DashboardTab.DASHBOARDS,
  selectedTargets: {},
};

/**
 * Migrates old selectedTargets format (string values) to new ComponentMetadata format (objects)
 * This ensures backward compatibility when loading from localStorage
 */
const migratePersistedState = (persistedState: unknown): State => {
  if (!persistedState || typeof persistedState !== 'object') {
    return { ...initialState };
  }

  const state = persistedState as State;

  // If there's no selectedTargets, return as-is
  if (!state.selectedTargets || typeof state.selectedTargets !== 'object') {
    return state;
  }

  const migratedTargets: WidgetMetadata = {};
  let needsMigration = false;

  // Check each dashboard
  Object.keys(state.selectedTargets).forEach((dashboardId) => {
    const dashboard = state.selectedTargets[dashboardId];
    if (!dashboard || typeof dashboard !== 'object') return;

    migratedTargets[dashboardId] = {};

    // Check each component
    Object.keys(dashboard).forEach((componentId) => {
      const value = dashboard[componentId];

      // If it's a string (old format), migrate to new format
      if (typeof value === 'string') {
        needsMigration = true;
        migratedTargets[dashboardId][componentId] = {
          selectedTargetId: value,
          // showTrendLines will default to undefined (falsy) for migrated data
        };
      } else {
        // Already in new format, keep as-is
        migratedTargets[dashboardId][componentId] = value;
      }
    });
  });

  // Return migrated state if migration was needed
  if (needsMigration) {
    return {
      ...state,
      selectedTargets: migratedTargets,
    };
  }

  return state;
};

export const useDashboardsStore = create<State>()(
  devtools(
    persist(
      logger(() => ({ ...initialState })),
      {
        name: 'dashboards-store',
        storage: createSafeJSONStorage(() => localStorage),
        version: 1,
        migrate: migratePersistedState,
      },
    ),
    { name: 'Bloomfilter - Dashboards Store', enabled: isLoggingEnabled },
  ),
);
